import { Input } from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { queryClient } from '../../../queryClient';
import { flushSync } from 'react-dom';
import { GetPlanningSessionResponse, updatePlanningSession } from '../../../api/planning.api';
import '../PlanningSession.scss';
import { updateSprint } from '../../../api/workspace';
import { useGetWorkspace } from '../../../serverState/workspace';
import { EditOutlined } from '@ant-design/icons';

export type SprintNameTitleInputProps = {
  sprintId?: number;
  planningSessionId?: number;
  sprintName?: string;
};

export const SprintNameTitleInput = forwardRef(
  ({ sprintId, planningSessionId, sprintName }: SprintNameTitleInputProps, ref) => {
    const workspaceQuery = useGetWorkspace();
    const spaceId = workspaceQuery.data?.id;

    const [sprintTitle, setSprintTitle] = useState(sprintName);
    const [editing, setEditing] = useState(false);

    const inputRef = useRef(null);

    useEffect(() => {
      if (editing && inputRef.current) {
        inputRef.current.focus();
      }

      return () => {};
    }, [editing]);

    useImperativeHandle(ref, () => ({
      triggerEdit: () => {
        setEditing(true);
      },
    }));

    const updateSprintName = async (e) => {
      const newValue = e.target.value;
      const trimmedNewValue: string = newValue.trim();
      if (!trimmedNewValue || (!planningSessionId && !sprintId)) {
        setEditing(false);
        return;
      }

      if (planningSessionId) {
        await updatePlanningSession(planningSessionId, {
          nextSprintName: trimmedNewValue,
        });

        flushSync(() => {
          queryClient.setQueryData(
            ['workspacePlanningSession', parseInt(spaceId as string)],
            (old: GetPlanningSessionResponse) => {
              const newPlaningSession = old;
              newPlaningSession.session.nextSprintName = trimmedNewValue;
              return newPlaningSession;
            }
          );

          setSprintTitle(trimmedNewValue);
          setEditing(false);
        });
      } else if (sprintId) {
        await updateSprint(parseInt(spaceId as string), sprintId, trimmedNewValue);

        flushSync(() => {
          setSprintTitle(trimmedNewValue);
          setEditing(false);
        });
      }
    };

    return (
      <div className="transform translate-x-[-4px]">
        {editing ? (
          <div>
            <Input
              className="text-brand-grey-dark text-base font-semibold leading-normal"
              size={'small'}
              ref={inputRef}
              defaultValue={sprintTitle || sprintName}
              onPressEnter={updateSprintName}
              onBlur={updateSprintName}
              onKeyDown={(e) => {
                if (e.key === 'Escape' || e.keyCode === 27) {
                  setEditing(false);
                }
              }}
            />
          </div>
        ) : (
          <div
            className="sprintNameTitle flex gap-2 text-brand-grey-dark text-base font-semibold leading-normal cursor-text px-1.5 rounded"
            onClick={() => setEditing(true)}
          >
            {sprintTitle || sprintName}
            <EditOutlined className="text-neutral-900" />
          </div>
        )}
      </div>
    );
  }
);
